import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface Project {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  is_active: boolean;
}

interface EvidenceItem {
  id: number;
  schedule_id: number | null;
  project_id: number | null;
  uploaded_by: number;
  original_name: string;
  mime_type: string | null;
  size: number | null;
  processed_at: number | null;
  processing_attempts: number;
  last_error: string | null;
  url: string;
}

const emptyForm = {
  id: null as number | null,
  name: '',
  code: '',
  description: '',
  is_active: true,
};

const ProjectsPage: React.FC = () => {
  const [items, setItems] = useState<Project[]>([]);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [evidences, setEvidences] = useState<EvidenceItem[]>([]);
  const [evidencesLoading, setEvidencesLoading] = useState(false);
  const [evidencesError, setEvidencesError] = useState<string | null>(null);
  const [evidenceStats, setEvidenceStats] = useState<{ total: number; pending: number; error: number; processed: number } | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [evidencesFilter, setEvidencesFilter] = useState<'all' | 'pending' | 'error'>('all');

  const loadProjects = () => {
    apiClient.get('/projects').then(res => {
      setItems(res.data.data.items as Project[]);
    });
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const startCreate = () => {
    setError(null);
    setForm(emptyForm);
  };

  const startEdit = (project: Project) => {
    setError(null);
    setForm({
      id: project.id,
      name: project.name,
      code: project.code ?? '',
      description: project.description ?? '',
      is_active: project.is_active,
    });
  };

  const selectProjectForEvidences = async (project: Project) => {
    setSelectedProjectId(project.id);
    setEvidences([]);
    setEvidencesError(null);
    setEvidenceStats(null);
    setUploadFile(null);

    setEvidencesLoading(true);
    try {
      const res = await apiClient.get<{ data: { items: EvidenceItem[] } }>('/evidences', {
        params: { project_id: project.id, pageSize: 50 },
      });
      setEvidences(res.data.data.items);
    } catch (e) {
      setEvidencesError('No se pudieron cargar las evidencias');
    } finally {
      setEvidencesLoading(false);
    }

    try {
      const statsRes = await apiClient.get<{
        data: { total: number; pending: number; error: number; processed: number };
      }>('/evidences/stats', {
        params: { project_id: project.id },
      });
      setEvidenceStats(statsRes.data.data);
    } catch (e) {
      setEvidenceStats(null);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const { name } = target;

    let value: string | boolean = (
      target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    ).value;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      value = target.checked;
    }

    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: form.name,
        code: form.code || null,
        description: form.description || null,
        is_active: form.is_active,
      };

      if (form.id == null) {
        await apiClient.post('/projects', payload);
      } else {
        await apiClient.put(`/projects/${form.id}`, payload);
      }

      setForm(emptyForm);
      loadProjects();
    } catch (err) {
      setError('Error guardando el proyecto');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (project: Project) => {
    if (!window.confirm(`Desactivar proyecto ${project.name}?`)) {
      return;
    }
    try {
      await apiClient.delete(`/projects/${project.id}`);
      loadProjects();
    } catch (err) {
      alert('No se pudo desactivar el proyecto');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    setUploadFile(file ?? null);
  };

  const handleUploadEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId || !uploadFile) return;

    setUploadLoading(true);
    setEvidencesError(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('project_id', String(selectedProjectId));

    try {
      const res = await apiClient.post<{ data: EvidenceItem }>('/evidences/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEvidences(prev => [res.data.data, ...prev]);
      setUploadFile(null);
    } catch (e) {
      setEvidencesError('Error al subir la evidencia');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteEvidence = async (id: number) => {
    if (!window.confirm('¿Eliminar esta evidencia?')) return;

    try {
      await apiClient.delete(`/evidences/${id}`);
      setEvidences(prev => prev.filter(ev => ev.id !== id));
    } catch (e) {
      setEvidencesError('No se pudo eliminar la evidencia');
    }
  };

  const handleReprocessEvidence = async (id: number) => {
    try {
      await apiClient.post(`/evidences/${id}/reprocess`);
      if (selectedProjectId) {
        try {
          const [evidencesRes, statsRes] = await Promise.all([
            apiClient.get<{ data: { items: EvidenceItem[] } }>('/evidences', {
              params: { project_id: selectedProjectId, pageSize: 50 },
            }),
            apiClient.get<{
              data: { total: number; pending: number; error: number; processed: number };
            }>('/evidences/stats', {
              params: { project_id: selectedProjectId },
            }),
          ]);
          setEvidences(evidencesRes.data.data.items);
          setEvidenceStats(statsRes.data.data);
          setEvidencesError(null);
        } catch (e) {
          // Ignoramos errores de refresco
        }
      }
    } catch (e) {
      setEvidencesError('No se pudo reencolar el procesamiento de la evidencia');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Proyectos
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Gestiona los proyectos que pueden recibir auditorías y evidencias.
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {/* Formulario de proyecto */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nuevo proyecto' : `Editar proyecto #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nuevo
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Nombre
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Nombre del proyecto"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Código
              </label>
              <input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="Código interno (opcional)"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Descripción
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                placeholder="Descripción breve del proyecto (opcional)"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                Activo
              </label>
            </div>

            {error && (
              <div
                style={{
                  color: '#fecaca',
                  background: 'rgba(220, 38, 38, 0.12)',
                  border: '1px solid rgba(248, 113, 113, 0.4)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  fontSize: 12,
                  marginBottom: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '8px 14px',
                borderRadius: 999,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                boxShadow: loading
                  ? '0 0 0 rgba(0,0,0,0)'
                  : '0 12px 30px rgba(22, 163, 74, 0.55)',
                opacity: loading ? 0.9 : 1,
              }}
            >
              {loading ? 'Guardando...' : 'Guardar proyecto'}
            </button>
          </form>
        </section>

        {/* Listado de proyectos */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de proyectos
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Total: {items.length}</span>
          </div>

          {items.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
              No hay proyectos registrados.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Nombre</th>
                    <th style={{ padding: '6px 4px' }}>Código</th>
                    <th style={{ padding: '6px 4px' }}>Activo</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(project => (
                    <tr
                      key={project.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{project.id}</td>
                      <td style={{ padding: '6px 4px' }}>{project.name}</td>
                      <td style={{ padding: '6px 4px' }}>{project.code}</td>
                      <td style={{ padding: '6px 4px' }}>{project.is_active ? 'Sí' : 'No'}</td>
                      <td style={{ padding: '6px 4px' }}>
                        <button
                          type="button"
                          onClick={() => startEdit(project)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(148, 163, 184, 0.7)',
                            backgroundColor: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 11,
                            cursor: 'pointer',
                            marginRight: 4,
                          }}
                        >
                          Editar
                        </button>
                        {project.is_active && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(project)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(248, 113, 113, 0.6)',
                              backgroundColor: 'rgba(127, 29, 29, 0.2)',
                              color: '#fecaca',
                              fontSize: 11,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          >
                            Desactivar
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => selectProjectForEvidences(project)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(59, 130, 246, 0.7)',
                            backgroundColor: 'rgba(30, 64, 175, 0.35)',
                            color: '#bfdbfe',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Evidencias
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Evidencias del proyecto */}
      {selectedProjectId && (
        <section
          style={{
            marginTop: 24,
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <h2
            style={{
              margin: '0 0 10px',
              fontSize: 15,
              fontWeight: 600,
              color: '#e5e7eb',
            }}
          >
            Evidencias del proyecto #{selectedProjectId}
          </h2>
          {evidenceStats && (
            <div style={{ marginBottom: 8, fontSize: 12 }}>
              <strong>Resumen evidencias:</strong>{' '}
              total {evidenceStats.total} · pendientes {evidenceStats.pending} · con error{' '}
              {evidenceStats.error} · procesadas {evidenceStats.processed}
            </div>
          )}
          <form onSubmit={handleUploadEvidence} style={{ marginBottom: 12 }}>
            <input type="file" onChange={handleFileChange} />{' '}
            <button
              type="submit"
              disabled={uploadLoading || !uploadFile}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: 'none',
                fontSize: 12,
                fontWeight: 500,
                cursor: uploadLoading || !uploadFile ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  uploadLoading || !uploadFile
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
              }}
            >
              {uploadLoading ? 'Subiendo...' : 'Subir evidencia'}
            </button>
          </form>
          {evidencesError && (
            <div style={{ color: '#fecaca', marginBottom: 8, fontSize: 12 }}>
              {evidencesError}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12 }}>
              Filtro evidencias:{' '}
              <select
                value={evidencesFilter}
                onChange={e => setEvidencesFilter(e.target.value as 'all' | 'pending' | 'error')}
                style={{
                  padding: '4px 8px',
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  color: '#e5e7eb',
                  fontSize: 12,
                }}
              >
                <option value="all">Todas</option>
                <option value="pending">Solo pendientes</option>
                <option value="error">Solo con error</option>
              </select>
            </label>
          </div>
          {evidencesLoading && <p style={{ fontSize: 12 }}>Cargando evidencias...</p>}
          {!evidencesLoading && evidences.length === 0 && (
            <p style={{ fontSize: 12 }}>No hay evidencias para este proyecto.</p>
          )}
          {!evidencesLoading && evidences.length > 0 && (
            (() => {
              const filtered = evidences.filter(ev => {
                if (evidencesFilter === 'pending') {
                  return !ev.processed_at;
                }
                if (evidencesFilter === 'error') {
                  return !!ev.last_error;
                }
                return true;
              });
              if (filtered.length === 0) {
                return <p style={{ fontSize: 12 }}>No hay evidencias que coincidan con el filtro.</p>;
              }
              return (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    marginTop: 8,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                      }}
                    >
                      <th style={{ padding: '6px 4px' }}>ID</th>
                      <th style={{ padding: '6px 4px' }}>Nombre</th>
                      <th style={{ padding: '6px 4px' }}>MIME</th>
                      <th style={{ padding: '6px 4px' }}>Tamaño</th>
                      <th style={{ padding: '6px 4px' }}>Procesado</th>
                      <th style={{ padding: '6px 4px' }}>Intentos</th>
                      <th style={{ padding: '6px 4px' }}>Error</th>
                      <th style={{ padding: '6px 4px' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(ev => (
                      <tr
                        key={ev.id}
                        style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                      >
                        <td style={{ padding: '6px 4px' }}>{ev.id}</td>
                        <td style={{ padding: '6px 4px' }}>{ev.original_name}</td>
                        <td style={{ padding: '6px 4px' }}>{ev.mime_type ?? '-'}</td>
                        <td style={{ padding: '6px 4px' }}>
                          {ev.size != null ? `${(ev.size / 1024).toFixed(1)} KB` : '-'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          {ev.processed_at
                            ? new Date(ev.processed_at * 1000).toLocaleString()
                            : 'Pendiente'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>{ev.processing_attempts}</td>
                        <td
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            padding: '6px 4px',
                          }}
                        >
                          {ev.last_error ?? '-'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <a
                            href={ev.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#38bdf8', textDecoration: 'none', marginRight: 4 }}
                          >
                            Ver/descargar
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteEvidence(ev.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(248, 113, 113, 0.6)',
                              backgroundColor: 'rgba(127, 29, 29, 0.2)',
                              color: '#fecaca',
                              fontSize: 11,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          >
                            Eliminar
                          </button>
                          {ev.last_error && (
                            <button
                              type="button"
                              onClick={() => handleReprocessEvidence(ev.id)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: 999,
                                border: '1px solid rgba(59, 130, 246, 0.7)',
                                backgroundColor: 'rgba(30, 64, 175, 0.35)',
                                color: '#bfdbfe',
                                fontSize: 11,
                                cursor: 'pointer',
                              }}
                            >
                              Reprocesar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )}
        </section>
      )}
    </div>
  );
}

export default ProjectsPage;
